import type { LocaleMessageValue, VueMessageType } from "vue-i18n"
import type { Languages } from "@/types/models/SharedModelTypes"
import { createI18n } from "vue-i18n"
import { z } from "zod"
import { isValidationErrorMessageKey } from "@/localization/messageKey"
import Norwegian from "@/localization/no-NO/master.json"
import { languageSchema } from "@/types/models/SharedModelTypes"
import { getLanguageBasedOnIP } from "@/utils/getLanguageBasedOnIp"

// Zod error map type
type ZodErrorMap = Parameters<typeof z.setErrorMap>[0]

const FALLBACK_LOCALE = "no-NO" satisfies Languages
const LANGUAGE_STORAGE_KEY = "tredco-app-language"

type LazyLanguage = Exclude<Languages, typeof FALLBACK_LOCALE>
type LocaleDictionary = Record<string, LocaleMessageValue<VueMessageType>>

/**
 * Norwegian is the fallback every missing key resolves to, so it ships in the
 * startup bundle. The other dictionaries are fetched when a user asks for
 * them; each is its own chunk.
 */
const lazyDictionaries: Record<LazyLanguage, () => Promise<LocaleDictionary>> = {
  "en-US": async () => import("@/localization/en-US/master.json").then(module => module.default),
  "fr-FR": async () => import("@/localization/fr-FR/master.json").then(module => module.default),
  "de-DE": async () => import("@/localization/de-DE/master.json").then(module => module.default),
  "fi-FI": async () => import("@/localization/fi-FI/master.json").then(module => module.default),
  "da-DK": async () => import("@/localization/da-DK/master.json").then(module => module.default),
}

/**
 * Until its dictionary arrives, a lazy language carries only its own name —
 * the `locale` key the language selector shows every language in — so all six
 * stay listed. A test holds these to the dictionaries' own `locale` values.
 */
const languageNameStubs: Record<LazyLanguage, LocaleDictionary> = {
  "en-US": { locale: "English" },
  "fr-FR": { locale: "Français" },
  "de-DE": { locale: "Deutsch" },
  "fi-FI": { locale: "Suomi" },
  "da-DK": { locale: "Dansk" },
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: FALLBACK_LOCALE,
  fallbackLocale: FALLBACK_LOCALE,
  silentFallbackWarn: true,
  messages: {
    [FALLBACK_LOCALE]: Norwegian,
    ...languageNameStubs,
  },
})

const t: typeof i18n.global.t = i18n.global.t

const loadedLocales = new Set<Languages>([FALLBACK_LOCALE])
const pendingLoads = new Map<Languages, Promise<DictionaryLoad>>()
let localeRequestSequence = 0

type DictionaryLoad = { isLoaded: true } | { isLoaded: false, error: unknown }

function isLazyLanguage(locale: Languages): locale is LazyLanguage {
  return locale !== FALLBACK_LOCALE
}

async function loadDictionary(locale: LazyLanguage): Promise<DictionaryLoad> {
  try {
    const dictionary = await lazyDictionaries[locale]()
    i18n.global.setLocaleMessage(locale, dictionary)
    loadedLocales.add(locale)
    return { isLoaded: true }
  }
  catch (error) {
    console.warn(`[i18n] Could not load the ${locale} dictionary`, error)
    return { isLoaded: false, error }
  }
}

/**
 * Resolves once `locale`'s full dictionary is installed, or with the error
 * that kept it out. Concurrent calls share one fetch; a failed fetch is not
 * cached, so the next call retries.
 */
async function ensureLocale(locale: Languages): Promise<DictionaryLoad> {
  if (!isLazyLanguage(locale) || loadedLocales.has(locale)) {
    return { isLoaded: true }
  }
  const pending = pendingLoads.get(locale)
  if (pending) {
    return pending
  }
  const load = loadDictionary(locale).then((result) => {
    pendingLoads.delete(locale)
    return result
  })
  pendingLoads.set(locale, load)
  return load
}

export type LocaleChangeResult
  = | { status: "applied" }
    | { status: "superseded" }
    | { status: "failed", error: unknown }

/**
 * Switches the app to `locale` only after its dictionary is in, so the screen
 * never shows Norwegian fallbacks in between. When requests overlap, the last
 * one wins: an earlier request that finishes loading later is dropped. A
 * failed load leaves the current language in place and hands back its error.
 */
export async function setAppLocale(locale: Languages): Promise<LocaleChangeResult> {
  const request = ++localeRequestSequence
  const load = await ensureLocale(locale)
  if (request !== localeRequestSequence) {
    return { status: "superseded" }
  }
  if (!load.isLoaded) {
    return { status: "failed", error: load.error }
  }
  i18n.global.locale.value = locale
  return { status: "applied" }
}

function readRequestedLocale(): Languages | null {
  // A `?language=` parameter wins (the PDF renderer and the subscription page
  // are opened with one); it is not stored.
  const urlLanguage = new URLSearchParams(window.location.search).get("language")
  if (urlLanguage) {
    const parsedLanguage = languageSchema.safeParse(urlLanguage)
    if (parsedLanguage.success) {
      return parsedLanguage.data
    }
    console.warn("Invalid language code in URL, falling back to stored language")
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (storedLanguage === null) {
    return null
  }
  // An unknown stored value kept the Norwegian fallback before dictionaries
  // were lazy; it still does.
  const parsedStored = languageSchema.safeParse(storedLanguage)
  return parsedStored.success ? parsedStored.data : FALLBACK_LOCALE
}

/**
 * First run: nothing stored, so the language follows the IP's country. The
 * app is not held for the lookup; it shows Norwegian until the answer arrives,
 * unless a language was asked for in the meantime.
 */
async function detectLocaleFromIp(): Promise<void> {
  const sequenceAtStart = localeRequestSequence
  const { language } = await getLanguageBasedOnIP()
  if (localeRequestSequence !== sequenceAtStart) {
    return
  }
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  await setAppLocale(language)
}

export type InitialLocaleResult = LocaleChangeResult | { status: "detecting" }

let initialLocale: Promise<InitialLocaleResult> | undefined

/**
 * The language a page starts in, from `?language=`, the stored choice or —
 * on first run — the IP lookup. Entries await it before mounting so the first
 * paint is already in the requested language. Idempotent, and it never
 * rejects: a throw on the way (storage a WebView refuses) is a failed load,
 * so each entry's failure policy applies instead of a page that never mounts.
 */
export async function initializeLocale(): Promise<InitialLocaleResult> {
  initialLocale ??= startInitialLocale()
  return initialLocale
}

async function startInitialLocale(): Promise<InitialLocaleResult> {
  try {
    const requestedLocale = readRequestedLocale()
    if (requestedLocale) {
      return await setAppLocale(requestedLocale)
    }
  }
  catch (error) {
    return { status: "failed", error }
  }
  void detectLocaleFromIp()
  return { status: "detecting" }
}

function readZodIssueMessageKey(message: string | undefined): string | undefined {
  if (message && isValidationErrorMessageKey(message)) {
    return message
  }
  return undefined
}

function resolveZodFieldLabel(path: readonly PropertyKey[] | undefined): string | undefined {
  const fieldName = path?.[0]
  if (fieldName === "qty") {
    return t("post_labels.quantity")
  }
  if (fieldName === "unit_price") {
    return t("post_labels.unit_price")
  }
  if (fieldName === "discount_amount") {
    return t("post_labels.discount")
  }
  return undefined
}

// Configure Zod error messages
const zodErrorMap: ZodErrorMap = (issue, ctx) => {
  let message: string
  const fieldLabel = resolveZodFieldLabel(issue.path)

  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === "undefined" || issue.received === "null") {
        message = t("shared.validations.required")
      }
      else {
        message = t("shared.validations.invalid_type", { expected: issue.expected, received: issue.received })
      }
      break
    case z.ZodIssueCode.too_small: {
      const messageKey = readZodIssueMessageKey(issue.message)
      if (messageKey) {
        message = t(messageKey, fieldLabel ? { field: fieldLabel } : {})
      }
      else {
        message = issue.type === "string"
          ? t("shared.validations.required", { minimum: issue.minimum })
          : t("shared.validations.min_value", { minimum: issue.minimum })
      }
      break
    }
    case z.ZodIssueCode.too_big:
      message = issue.type === "string"
        ? t("shared.validations.max_length", { maximum: issue.maximum })
        : t("shared.validations.max_value", { maximum: issue.maximum })
      break
    case z.ZodIssueCode.not_multiple_of: {
      const messageKey = readZodIssueMessageKey(issue.message)
      if (messageKey) {
        message = t(messageKey, fieldLabel ? { field: fieldLabel } : {})
      }
      else {
        message = ctx.defaultError
      }
      break
    }
    case z.ZodIssueCode.invalid_string:
      if (issue.validation === "email") {
        message = t("shared.validations.email")
      }
      else if (issue.validation === "url") {
        message = t("shared.validations.url")
      }
      else {
        message = t("shared.validations.invalid_string")
      }
      break
    default: {
      const messageKey = readZodIssueMessageKey(issue.message)
      message = messageKey
        ? t(messageKey, fieldLabel ? { field: fieldLabel } : {})
        : ctx.defaultError
    }
  }

  return { message }
}

z.setErrorMap(zodErrorMap)

export { i18n, t }
